import { get, isNil, isNull, some } from 'lodash-es';
import type { Observable, OperatorFunction } from 'rxjs';
import { combineLatestWith, map, merge, of, pipe, startWith, Subscription, switchMap } from 'rxjs';
import type { VperBucket } from '@/graphql/types';
import { NotImplementedError } from '../../utils/errors';
import { applyNamingConvention } from '../../utils/naming-conventions';
import { convertDistanceAndFloat, getDataType } from '../data-types';
import { SWARM_TYPE_DATA_TYPES, VIBRATION_GRAPH_DATA_TYPES } from '../constants';
import { DATA_TYPE, type DataTypeValue } from '../enums';
import { SwarmType } from '../../enums';
import { getOverrides, getTooltipHeader } from '../graph-helpers';
import { periodTranslations } from '../vper-table';
import { shareReplayRefCount } from '../../utils/rxjs';
import { timestampRangeOverlap } from '../../utils/range';
import type {
    EventRecord,
    Graph,
    SampleRecord,
    SensorDataSetGraph,
    Trace,
    ValueFrequencyGraph,
    ValueTimeGraph,
} from '../graph';
import type { DataSet } from '../dataset';
import type { LegendData } from '../legend-helpers';

const VEFF_DATA_TYPE: DataTypeValue = DATA_TYPE.VEFF;

interface AxisElement {
    value: number | null;
}

interface RecordWithValue {
    value: number | null;
}

interface ElementWithAxis {
    axis: string;
}

export function valueNotNull(a: RecordWithValue): boolean {
    return !isNull(a.value);
}

export function sortAxis(a: AxisElement, b: AxisElement): number {
    if (a.value && b.value) {
        return b.value - a.value;
    }

    return 0;
}

export function filterElementsOnAxesWithObservable<T extends ElementWithAxis>(
    observable$: Observable<string[]>
): OperatorFunction<T[], T[]> {
    return pipe(
        combineLatestWith(observable$),
        map(([elements, visibleTypes]) =>
            elements.filter((element) => visibleTypes.includes(element.axis))
        )
    );
}

export function filterElementsOnAxesWithLegend<T extends ElementWithAxis>(
    legendData: LegendData
): OperatorFunction<T[], T[]> {
    return filterElementsOnAxesWithObservable(legendData.visibleTypes$);
}

export class TooltipPoint {
    position: GraphElementPosition;

    constructor(position: GraphElementPosition) {
        this.position = position;
    }

    get x(): number {
        return this.position.x;
    }

    get y(): number {
        return this.position.y;
    }
}

export class GraphElementPosition {
    element: BaseGraphElement;

    x: number;

    y: number;

    constructor(element: BaseGraphElement, x: number, y: number) {
        this.element = element;
        this.x = x;
        this.y = y;
    }

    get record(): SampleRecord | EventRecord {
        return this.element.record;
    }

    get timestamp(): number {
        return this.element.timestamp;
    }

    get axis(): string | undefined {
        return this.element.axis;
    }

    static findX(graph: ValueTimeGraph | ValueFrequencyGraph, element: BaseGraphElement): number {
        return graph.scaleX(element);
    }

    static findY(graph: ValueTimeGraph | ValueFrequencyGraph, element: BaseGraphElement): number {
        return graph.scaleY(element);
    }

    static createForElement(
        element: BaseGraphElement,
        graph: ValueFrequencyGraph | ValueTimeGraph
    ): GraphElementPosition | null {
        const x = this.findX(graph, element);
        const y = this.findY(graph, element);

        if (Number.isNaN(x) || Number.isNaN(y)) {
            return null;
        }

        return new this(element, x, y);
    }

    getToolTipCircleType() {
        return this.axis!;
    }

    getToolTipPoints() {
        return [new TooltipPoint(this)];
    }

    // This method allows, for example, when hovering with your mouse
    // on an x axis element, the other axes are also highlighted.
    findAssociatedPositions(positions: GraphElementPosition[]): GraphElementPosition[] {
        return positions.filter((position) => position.record === this.record);
    }

    getToolTipCircles$(
        positions$: Observable<GraphElementPosition[]>
    ): Observable<Array<{ type: string; transform: string }>> {
        return positions$.pipe(
            map((positions) =>
                this.findAssociatedPositions(positions).flatMap((position) =>
                    position.getToolTipPoints().map((point) => ({
                        type: point.position.getToolTipCircleType(),
                        transform: `translate(${point.x}, ${point.y})`,
                    }))
                )
            )
        );
    }
}

const vperPeriodBucketTypeNames = ['KBFTrBucket', 'VperBucket'];

function sampleToText(
    s: SampleRecord | EventRecord | VperBucket,
    graph: SensorDataSetGraph,
    axes: string[],
    types: Array<string | RegExp>
): string {
    let text = '';

    // Determine if we are dealing with an air sample
    const isAir = SWARM_TYPE_DATA_TYPES[SwarmType.Air].includes(
        graph.getDisplayedDataType().key as DataTypeValue
    );

    axes.forEach((axis) => {
        types.forEach((type) => {
            // Determine the value getter for air or others.
            const valueGetter = isAir ? axis : [type, axis];

            if (isAir && type instanceof RegExp && !type.test(axis)) {
                return;
            }

            const value = get(s, valueGetter as keyof typeof s) as number;

            // Make sure it is actually a value.
            if (isNil(value)) {
                return;
            }

            // Determine which name to get.
            const valueName = isAir ? axis : (type as string);

            // Get the actual unit, title and converted value.
            const dataType = getDataType(valueName);
            const { title } = dataType;
            const unit = dataType.getUnit(graph.profile);
            const convertedValue = convertDistanceAndFloat(valueName, value, graph.profile);

            // Construct the title for this value
            const valueTitle = isAir ? title : [applyNamingConvention(title), axis].join(' ');

            // Construct the actual entry for the tooltip.
            text += `${valueTitle}: ${[convertedValue, unit].join(' ')}<br/>`;
        });
    });

    return text;
}

export class BaseGraphElement {
    record: SampleRecord | EventRecord;

    axis?: string;

    dataType?: DataTypeValue;

    private _value: number | null = null;

    public get value(): number | null {
        return this._value;
    }

    public set value(value: number | null) {
        this._value = value;
    }

    constructor(record: SampleRecord | EventRecord) {
        // Store the source record.
        this.record = record;
    }

    get timestamp(): number {
        return this.record.timestamp;
    }

    static createCollectionFromRecords(
        this: new (record: SampleRecord | EventRecord) => BaseGraphElement,
        records: Array<SampleRecord | EventRecord>,
        ..._args: unknown[]
    ): BaseGraphElement[] {
        return records.map((record) => new this(record));
    }

    getTooltipVisibleAxes(legendData: LegendData): string[] {
        return legendData.getVisibleTypes();
    }

    getTooltipVisibleData(graph: SensorDataSetGraph): Array<string | RegExp> | undefined {
        return graph.getDisplayedDataType().visibleData;
    }

    timespanContainsTraces(timespan: { from: number; to: number }, dataSet: DataSet): boolean {
        return some((dataSet as DataSet & { traces: Trace[] }).traces, (trace: Trace) =>
            timestampRangeOverlap([timespan.from, timespan.to], [trace.start, trace.end])
        );
    }

    getVperPeriodForTimestamp(_timestamp: number, _dataSet: DataSet): VperBucket | null {
        // This method needs to be implemented in subclasses or return null.
        return null;
    }

    getToolTipText(dataSet: DataSet, graph: SensorDataSetGraph): string {
        const sample = this.record;
        const { sensor, timezone } = dataSet;

        let toolTipText = getTooltipHeader(this.timestamp, timezone, sensor);

        // Bucket balloon.
        if (vperPeriodBucketTypeNames.includes(sample.__typename!)) {
            const { period } = sample as SampleRecord & { period: string };
            toolTipText += `${gettext('Period')}: ${periodTranslations[period].toLowerCase()}<br/>`;
        }

        // Bucket balloon.
        if (
            ['SampleBucket', 'Dust', 'DustMeta', ...vperPeriodBucketTypeNames].includes(
                sample.__typename!
            )
        ) {
            const visibleAxes = this.getTooltipVisibleAxes(graph.legendData);
            const visibleData = this.getTooltipVisibleData(graph);

            toolTipText += sampleToText(sample, graph, visibleAxes, visibleData!);

            // Show Vper values inside a Veff SampleBucket tooltip.
            if (sample.__typename === 'SampleBucket' && dataSet.dataType === VEFF_DATA_TYPE) {
                const vperPeriodSample = this.getVperPeriodForTimestamp(this.timestamp, dataSet);

                if (vperPeriodSample) {
                    toolTipText += sampleToText(vperPeriodSample, graph, ['x', 'y', 'z'], ['vper']);
                }
            }

            // These data types don't do traces.
            if (VIBRATION_GRAPH_DATA_TYPES.includes(dataSet.dataType)) {
                const { timespan } = sample as SampleRecord & {
                    timespan: { from: number; to: number };
                };
                const containsTraces = this.timespanContainsTraces(timespan, dataSet);
                let traces: string;

                if (containsTraces) {
                    const overrides = getOverrides();

                    const url = graph.getShowTracesUrl!(
                        sensor,
                        timespan.from,
                        timespan.to,
                        dataSet.dataType,
                        overrides
                    );
                    const text = gettext('Click for traces');
                    traces = `<a href='${url}'>${text}</a>`;
                } else {
                    traces = gettext('No traces');
                }

                toolTipText += `Traces: ${traces}<br/>`;
            }
        }

        // Event balloon.
        if (sample.__typename?.startsWith('Event')) {
            const rowsToShow = ['name', 'stopCause', 'startMessage', 'uploading'];
            rowsToShow.forEach((type) => {
                const sampleValue = (sample as unknown as Record<string, string>)[type];
                if (!isNil(sampleValue)) {
                    const { title } = getDataType(type);
                    if (title) {
                        toolTipText += `${title}: `;
                    }
                    toolTipText += `${sampleValue}<br/>`;
                }
            });
            if (
                sample.__typename === 'EventStart' &&
                !graph
                    .getDisplayedDataType()
                    .eventConfigMatcher?.(
                        sample as EventRecord & { config: Record<string, unknown> }
                    )
            ) {
                toolTipText += gettext('MP_NOT_MEASURING_TYPE');
            }
        }

        return toolTipText;
    }

    getPositionElementClass(): typeof GraphElementPosition {
        return GraphElementPosition;
    }

    createPosition(graph: Graph): GraphElementPosition | null {
        return this.getPositionElementClass().createForElement(
            this as unknown as BaseGraphElement,
            graph as ValueTimeGraph | ValueFrequencyGraph
        );
    }
}

export class BaseGraphPlugin {
    graph: Graph;

    subscription: Subscription;

    constructor(graph: Graph) {
        this.graph = graph;
        this.subscription = new Subscription();
    }

    get dataSet(): DataSet {
        return (this.graph as SensorDataSetGraph).data;
    }

    init(): void {
        throw new NotImplementedError();
    }
}

export class BaseGraphElementsProvider extends BaseGraphPlugin {
    isActive$: Observable<boolean>;

    graphElements$: Observable<BaseGraphElement[]>;

    graphElementPositions$: Observable<GraphElementPosition[]>;

    constructor(...args: [Graph]) {
        super(...args);

        this.isActive$ = this.createIsActiveObservable();

        this.graphElements$ = (
            this.constructor as typeof BaseGraphElementsProvider
        ).getGraphElementsObservableForDataSet(
            (this.graph as SensorDataSetGraph).data,
            (this.graph as SensorDataSetGraph).legendData
        );

        this.graphElementPositions$ = this.isActive$.pipe(
            switchMap((isActive) =>
                isActive ? this.createGraphElementPositionsObservable() : of([])
            )
        );
    }

    createIsActiveObservable(): Observable<boolean> {
        return of(true);
    }

    createGraphElementPositionsObservable(): Observable<GraphElementPosition[]> {
        return this.graphElements$.pipe(
            combineLatestWith(
                merge(this.graph.xAxis.scaleUpdated$, this.graph.yAxis.scaleUpdated$).pipe(
                    startWith(null)
                )
            ),
            map(([elements, _]) =>
                elements
                    .map((element) => element.createPosition(this.graph))
                    // The `createPosition` method can return null if an element cannot be
                    // positioned (e.g., a value below 1 on a logarithmic scale).
                    // We filter out these null values to prevent them from being rendered.
                    .filter((position): position is GraphElementPosition => position !== null)
            ),
            shareReplayRefCount()
        );
    }

    static createGraphElementsObservable(
        _dataSet: DataSet,
        _legendData: LegendData
    ): Observable<BaseGraphElement[]> {
        throw new NotImplementedError();
    }

    // Since the velocity/time and velocity/frequency graphs on the graph page share the same
    // LegendData and DataSet, we've implemented this caching step to share the 'elements to
    // show' calculation and filtering.
    static getGraphElementsObservableForDataSet(
        dataSet: DataSet,
        legendData: LegendData
    ): Observable<BaseGraphElement[]> {
        const { graphElementObservables } = dataSet;

        if (!graphElementObservables.has(this)) {
            graphElementObservables.set(this, new WeakMap());
        }

        const legendMap = graphElementObservables.get(this)!;

        if (!legendMap.has(legendData)) {
            const observable = this.createGraphElementsObservable(dataSet, legendData);
            legendMap.set(legendData, observable);
        }

        return legendMap.get(legendData)!;
    }
}
